import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import { useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Button } from '@/components/Button/Button';
import { MAX_CHARS } from '@/config/constants';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useSocket } from '@/hooks/useSocket';
import { useWidgetState } from '@/hooks/useWidgetState';
import { chatModalState, isMobileState } from '@/state/session';
import styles from './Survey.module.scss';
export const Survey = () => {
    const [activeButton, setActiveButton] = useState(null);
    const isMobile = useRecoilValue(isMobileState);
    const { trackEvent } = useAnalytics();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const surveyTexts = [
        t('survey.terrible'),
        t('survey.bad'),
        t('survey.okay'),
        t('survey.good'),
        t('survey.excellent'),
    ];
    const { sendRating, closeSession } = useSocket();
    const [chatModal, setChatModal] = useRecoilState(chatModalState);
    const inputRef = useRef(null);
    const [input, setInput] = useState('');
    const { closeWidget } = useWidgetState();
    const handleClose = useCallback(async () => {
        // Track review popover interaction on close
        setChatModal(null);
        closeSession();
        closeWidget();
    }, [closeSession, closeWidget, setChatModal]);
    useEffect(() => {
        if (chatModal === "survey-full" /* ChatModal.SurveyFull */ && inputRef.current) {
            setTimeout(() => {
                var _a;
                (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 50);
        }
    }, [chatModal]);
    const handleLimitInputChange = useCallback(() => {
        var _a, _b, _c;
        if (((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.textContent) &&
            ((_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.textContent.length) >= MAX_CHARS) {
            inputRef.current.textContent = (_c = inputRef.current) === null || _c === void 0 ? void 0 : _c.textContent.slice(0, MAX_CHARS);
            const selection = window.getSelection();
            const range = document.createRange();
            selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
            range.selectNodeContents(inputRef.current);
            range.collapse(false);
            selection === null || selection === void 0 ? void 0 : selection.addRange(range);
            inputRef.current.focus();
        }
        const element = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current;
        if (!(element === null || element === void 0 ? void 0 : element.textContent)) {
            const breakTag = element === null || element === void 0 ? void 0 : element.getElementsByTagName('br')[0];
            if (breakTag) {
                element === null || element === void 0 ? void 0 : element.removeChild(breakTag);
            }
        }
    }, []);
    const handleInputChange = useCallback(() => {
        var _a;
        handleLimitInputChange();
        setInput(((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.textContent) || '');
    }, [handleLimitInputChange]);
    const handleSubmit = useCallback(() => {
        if (activeButton !== null) {
            trackEvent(AnalyticsEvent.RatingSubmitted);
            setIsLoading(true);
            sendRating({ rating: activeButton + 1, feedback: input });
        }
    }, [activeButton, input, sendRating, trackEvent]);
    const handleRatingClick = (index) => {
        // Track review popover interaction when a rating is clicked
        trackEvent(AnalyticsEvent.RatingClicked, { rating: index + 1 });
        setActiveButton(index);
        setChatModal("survey-full" /* ChatModal.SurveyFull */);
    };
    const handleSkipAndClose = useCallback(() => {
        trackEvent(AnalyticsEvent.PopoverReviewSkipAndCloseClicked);
        handleClose();
    }, [handleClose, trackEvent]);
    const handleFeedbackClose = useCallback(() => {
        trackEvent(AnalyticsEvent.PopoverFeedbackCloseClicked);
        handleClose();
    }, [handleClose, trackEvent]);
    return (_jsxs("div", { className: styles.form, children: [_jsxs("div", { className: styles.surveyWrapper, children: [_jsx("div", { className: styles.title, children: _jsx("span", { children: t('survey.experience') }) }), _jsx("div", { className: styles.surveyContainer, children: Array(5)
                            .fill('')
                            .map((_, index) => (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                        _jsxs("div", { className: cn(styles.circleContainer, {
                                [styles.active]: activeButton === index,
                            }), onClick: () => handleRatingClick(index), role: "button", tabIndex: 0, children: [_jsx("button", { className: cn(styles.circleBtn, {
                                        [styles.hideDescription]: (index !== 0 && index !== 4) || activeButton,
                                    }), children: index + 1 }), _jsx("span", { children: surveyTexts[index] })] }, index))) })] }), chatModal === "survey-full" /* ChatModal.SurveyFull */ && (_jsxs("div", { className: styles.feedback, children: [activeButton && activeButton > 2 ? (_jsx("p", { children: t('survey.well') })) : (_jsx("p", { children: t('survey.wrong') })), _jsx("div", { ref: inputRef, className: cn(styles.input, {
                            [styles.isMobile]: isMobile,
                        }), "aria-label": "input", role: "textbox", tabIndex: 0, "data-placeholder": t('survey.type_here'), contentEditable: true, onInput: handleInputChange }), _jsx("span", { "data-testid": "char-counter", className: styles.charCounter, children: MAX_CHARS - input.length })] })), activeButton !== null ? (_jsxs("div", { className: styles.buttonContainer, children: [_jsx(Button, { onClick: handleSubmit, isLoading: isLoading, children: t('buttons.submit') }), _jsx(Button, { variant: "secondary", onClick: handleFeedbackClose, children: t('buttons.close') })] })) : (_jsx("button", { className: styles.close, onClick: handleSkipAndClose, children: t('buttons.skip_close') }))] }));
};
